import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { formatCentsToDollarString } from '@fhs/utils';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';

import { useUserAccountsQuery } from '../queries/use-user-accounts';

export type GiftCardContext = {
  formattedBalance: string;
  balanceUpdated: boolean;
  handleBalanceUpdate: (newBalance: number) => void;
  handleError: () => void;
  isDigitalBalanceLoading: boolean;
  currentBalance: number;
  lastDigits: string;
  hasGiftCard: boolean;
};

const giftCardContext = createContext<null | GiftCardContext>(null);

export type GiftCardProviderProps = {
  children?: React.ReactNode;
};

export const GiftCardProvider = (props: GiftCardProviderProps) => {
  const [balanceUpdated, setBalanceUpdated] = useState(false);
  const [newBalance, setNewBalance] = useState(0);

  const { data, isPending: isDigitalBalanceLoading } = useUserAccountsQuery();
  const giftCard = data?.accounts?.find(account => account.giftCard)?.giftCard;
  const currentBalance = newBalance || giftCard?.currentBalance || 0;

  const formattedBalance = useMemo(
    () => `$${formatCentsToDollarString(currentBalance)}`,
    [currentBalance]
  );

  const { show } = useToast();

  const handleBalanceUpdate = useCallback(
    (updatedBalance: number) => {
      setNewBalance(updatedBalance);
      setBalanceUpdated(true);
      setTimeout(() => {
        setBalanceUpdated(false);
      }, 100);

      show({
        variant: 'positive',
        text: 'Digital gift card funds updated!',
      });
    },
    [show]
  );

  const handleError = useCallback(() => {
    show({
      variant: 'negative',
      text: 'Error updating digital gift card funds',
    });
  }, [show]);

  return (
    <giftCardContext.Provider
      value={{
        formattedBalance,
        balanceUpdated,
        handleBalanceUpdate,
        handleError,
        isDigitalBalanceLoading,
        currentBalance,
        lastDigits: giftCard?.last4 ?? '',
        hasGiftCard: Boolean(giftCard),
      }}
    >
      {props.children}
    </giftCardContext.Provider>
  );
};

export function useGiftCardContext() {
  const context = useContext(giftCardContext);

  if (!context) {
    throw new Error('Must consume `useGiftCardContext` with a GiftCardProvider');
  }

  return context;
}
